import { useRef, useCallback, useEffect } from 'react';

import stringify from 'fast-json-stable-stringify';
import { useDispatch, useSelector } from 'react-redux';

import { queryNotRequired, queryRequired } from '../../../actions/network';
import { getQuery } from '../../../state/shared/network';

import type { DataRequest, QueryState } from './types';
import type { QueryType } from '@web-platform/shared-universal-types/src/network/requestTypes';
import type { StateType as State } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

/**
 * Use this hook to schedule an HTTP request to fetch some data
 *
 * Its input is a DataRequest object, which has 4 properties:
 * - type: name of the API to call
 * - variables: similar to an HTTP request body
 * - request: similar to Axios request arguments
 * - nextRequestAt: timestamp when the next request is to be made to update the data being fetched. If this is null, no follow-up request will be made
 *
 * The type and variable properties are combined to form the key for the request. If these ever change, the previous request is cancelled and a new one is scheduled using the new values
 *
 * If the hook is given a null data request, the current one will be cancelled
 *
 * The hook outputs the current state of the request that has been scheduled. This object contains the following property:
 * - moreDataExpected: is this component waiting for data to be fetched? Can be used by child components to render fallback content (e.g. loading spinner)
 *
 * @param {Nullable<DataRequest>} dataRequest What HTTP request do we want to make and when?
 * @returns {QueryState} Current state of the scheduled HTTP request
 */
export const useQuery = <QT extends QueryType>(
  dataRequest: Nullable<DataRequest & { type: QT }>,
): QueryState<QT> => {
  const dispatch = useDispatch();
  const query = useSelector((state: State) =>
    dataRequest
      ? getQuery(state.shared.network, dataRequest.type, dataRequest.variables)
      : undefined,
  );
  const isQuerySending = !!query?.inFlight;

  const dataRequestRef = useRef<Nullable<DataRequest>>(null);

  if (stringify(dataRequestRef.current) !== stringify(dataRequest)) {
    dataRequestRef.current = dataRequest;
  }

  const currentDataRequest = dataRequestRef.current;

  const dispatchQueryRequired = useCallback(() => {
    if (currentDataRequest && !isQuerySending) {
      const { nextRequestAt, request, type, variables } = currentDataRequest;

      dispatch(queryRequired(type, variables, request, nextRequestAt));
    }
  }, [currentDataRequest, isQuerySending, dispatch]);

  const dispatchQueryNotRequired = useCallback(() => {
    if (currentDataRequest) {
      const { nextRequestAt, type, variables } = currentDataRequest;

      dispatch(queryNotRequired(type, variables, nextRequestAt));
    }
  }, [currentDataRequest, dispatch]);

  useEffect(() => {
    dispatchQueryRequired();
    return dispatchQueryNotRequired;
  }, [dispatchQueryRequired, dispatchQueryNotRequired]);
  return {
    moreDataExpected: dataRequest === null ? true : !!dataRequest.nextRequestAt,
    query,
  };
};
